import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { faqService } from '../../../services/faqService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FAQManager = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    displayOrder: 0,
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Get predefined FAQ categories
  const predefinedCategories = faqService?.getFAQCategories() || [];
  
  // Get unique categories from actual FAQs
  const getUniqueCategoriesFromFAQs = () => {
    const uniqueCategories = new Set();
    faqs?.forEach(faq => {
      if (faq?.category) {
        uniqueCategories?.add(faq?.category);
      }
    });
    return Array.from(uniqueCategories);
  };

  // Combine predefined categories with actual FAQ categories
  const getAvailableCategories = () => {
    const actualCategories = getUniqueCategoriesFromFAQs();
    const categoryMap = new Map();
    
    // Add predefined categories
    predefinedCategories?.forEach(cat => {
      categoryMap?.set(cat?.value, cat);
    });
    
    // Add actual categories that might not be in predefined list
    actualCategories?.forEach(catValue => {
      if (!categoryMap?.has(catValue)) {
        categoryMap?.set(catValue, { value: catValue, label: catValue });
      }
    });
    
    return Array.from(categoryMap?.values());
  };

  const categories = getAvailableCategories();

  useEffect(() => {
    if (user) {
      fetchFAQs();
    }
  }, [user]);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const data = await faqService?.getInstituteFAQs(user?.id);
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast?.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.question?.trim()) {
      errors.question = 'Question is required';
    } else if (formData?.question?.trim()?.length < 10) {
      errors.question = 'Question must be at least 10 characters';
    }
    
    if (!formData?.answer?.trim()) {
      errors.answer = 'Answer is required';
    } else if (formData?.answer?.trim()?.length < 20) {
      errors.answer = 'Answer must be at least 20 characters';
    }
    
    if (!formData?.category) {
      errors.category = 'Category is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleOpenAddModal = () => {
    setFormData({
      question: '',
      answer: '',
      category: '',
      displayOrder: 0,
      isActive: true
    });
    setFormErrors({});
    setEditingFAQ(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (faq) => {
    setFormData({
      question: faq?.question || '',
      answer: faq?.answer || '',
      category: faq?.category || '',
      displayOrder: faq?.displayOrder || 0,
      isActive: faq?.isActive !== false
    });
    setFormErrors({});
    setEditingFAQ(faq);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingFAQ(null);
    setFormData({
      question: '',
      answer: '',
      category: '',
      displayOrder: 0,
      isActive: true
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      toast?.error('Please fix all validation errors');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingFAQ) {
        await faqService?.updateFAQ(editingFAQ?.id, {
          ...formData,
          instituteId: user?.id
        });
        toast?.success('FAQ updated successfully');
      } else {
        await faqService?.createFAQ({
          ...formData,
          instituteId: user?.id
        });
        toast?.success('FAQ added successfully');
      }
      
      handleCloseModal();
      fetchFAQs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast?.error(error?.message || 'Failed to save FAQ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (faqId) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await faqService?.deleteFAQ(faqId);
      toast?.success('FAQ deleted successfully');
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast?.error('Failed to delete FAQ');
    }
  };

  const handleToggleActive = async (faq) => {
    try {
      await faqService?.updateFAQ(faq?.id, {
        isActive: !faq?.isActive
      });
      toast?.success(`FAQ ${!faq?.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchFAQs();
    } catch (error) {
      console.error('Error toggling FAQ status:', error);
      toast?.error('Failed to update FAQ status');
    }
  };

  const filteredFAQs = faqs?.filter(faq => {
    const matchesCategory = filterCategory === 'all' || faq?.category === filterCategory;
    const matchesSearch = !searchQuery || 
      faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryStats = () => {
    const stats = {};
    // Count FAQs by category (including categories not in predefined list)
    faqs?.forEach(faq => {
      if (faq?.category) {
        stats[faq.category] = (stats?.[faq?.category] || 0) + 1;
      }
    });
    // Ensure all categories in the list have a count (even if 0)
    categories?.forEach(cat => {
      if (!(cat?.value in stats)) {
        stats[cat.value] = 0;
      }
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
            FAQ Manager
          </h2>
          <p className="text-muted-foreground">
            Manage frequently asked questions for students and AI chat
          </p>
        </div>
        <Button onClick={handleOpenAddModal} className="flex items-center gap-2">
          <Icon name="Plus" size={20} />
          <span>Add FAQ</span>
        </Button>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="HelpCircle" size={20} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground">Total FAQs</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{faqs?.length}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            <span className="text-sm text-muted-foreground">Active</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {faqs?.filter(f => f?.isActive)?.length}
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="XCircle" size={20} color="var(--color-destructive)" />
            <span className="text-sm text-muted-foreground">Inactive</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {faqs?.filter(f => !f?.isActive)?.length}
          </p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Tag" size={20} color="var(--color-accent)" />
            <span className="text-sm text-muted-foreground">Categories</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {Object.values(categoryStats)?.filter(count => count > 0)?.length}
          </p>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Search FAQs
            </label>
            <div className="relative">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
              />
              <Input
                type="text"
                placeholder="Search questions or answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Filter by Category
            </label>
            <Select
              value={filterCategory}
              onChange={(value) => setFilterCategory(value)}
              options={[
                { value: 'all', label: `All Categories (${faqs?.length || 0})` },
                ...(predefinedCategories?.length > 0 ? predefinedCategories?.map(cat => {
                  const count = categoryStats?.[cat?.value] || 0;
                  return {
                    value: cat?.value,
                    label: `${cat?.label} (${count})`
                  };
                }) : [])
              ]}
              placeholder="Select a category"
            />
          </div>
        </div>
      </div>
      {/* FAQ List */}
      {filteredFAQs?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="HelpCircle" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {faqs?.length === 0 ? 'No FAQs Yet' : 'No FAQs Found'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {faqs?.length === 0 
              ? 'Start by adding your first FAQ to help students and improve AI chat accuracy.' :'Try adjusting your filters or search query.'}
          </p>
          {faqs?.length === 0 && (
            <Button onClick={handleOpenAddModal}>
              Add Your First FAQ
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredFAQs?.map((faq) => (
            <div
              key={faq?.id}
              className={`bg-card border rounded-lg p-5 transition-all ${
                faq?.isActive ? 'border-border' : 'border-muted bg-muted/20'
              }`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {faq?.category}
                    </span>
                    {!faq?.isActive && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        Inactive
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {faq?.question}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {faq?.answer}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleActive(faq)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title={faq?.isActive ? 'Deactivate' : 'Activate'}
                  >
                    <Icon 
                      name={faq?.isActive ? 'Eye' : 'EyeOff'} 
                      size={18} 
                      color={faq?.isActive ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                  </button>
                  <button
                    onClick={() => handleOpenEditModal(faq)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Icon name="Edit" size={18} color="var(--color-primary)" />
                  </button>
                  <button
                    onClick={() => handleDelete(faq?.id)}
                    className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Order: {faq?.displayOrder}</span>
                <span>•</span>
                <span>Created: {new Date(faq?.createdAt)?.toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-heading font-bold text-foreground">
                {editingFAQ ? 'Edit FAQ' : 'Add New FAQ'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category *
                </label>
                <Select
                  value={formData?.category}
                  onChange={(value) => setFormData({ ...formData, category: value })}
                  options={[
                    { value: '', label: 'Select a category', disabled: true },
                    ...(predefinedCategories?.map(cat => ({
                      value: cat?.value,
                      label: cat?.label
                    })) || [])
                  ]}
                  placeholder="Select a category"
                  className={formErrors?.category ? 'border-destructive' : ''}
                />
                {formErrors?.category && (
                  <p className="text-destructive text-sm mt-1">{formErrors?.category}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Question *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., What is the admission process?"
                  value={formData?.question}
                  onChange={(e) => setFormData({ ...formData, question: e?.target?.value })}
                  className={formErrors?.question ? 'border-destructive' : ''}
                />
                {formErrors?.question && (
                  <p className="text-destructive text-sm mt-1">{formErrors?.question}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Answer *
                </label>
                <textarea
                  placeholder="Provide a detailed answer..."
                  value={formData?.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e?.target?.value })}
                  rows={6}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    formErrors?.answer ? 'border-destructive' : 'border-border'
                  }`}
                />
                {formErrors?.answer && (
                  <p className="text-destructive text-sm mt-1">{formErrors?.answer}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Display Order
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData?.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e?.target?.value) || 0 })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lower numbers appear first
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  <div className="flex items-center gap-2 h-10">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData?.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e?.target?.checked })}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <label htmlFor="isActive" className="text-sm text-foreground">
                      Active (visible to students)
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting ? 'Saving...' : (editingFAQ ? 'Update FAQ' : 'Add FAQ')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManager;